import { Injectable } from "@angular/core";
import { catchError, EMPTY, Observable, of, switchMap, tap } from "rxjs";
import { AUTH_KEYS, AuthProvider, AuthResponse, LoginType } from "../../interfaces/auth-response";
import { AzureAuthRequest, AzureAuthResponse, AzureInitRequest, AzureInitResponse, AzureTokenRequest, AzureTokenResponse } from "../../interfaces/azureLogin-interface";
import { HttpClient, HttpContext, HttpHeaders } from "@angular/common/http";
import { Router } from "@angular/router";
import { BYPASS_AUTH } from "../../interceptors/auth.interceptor";
import { CiamRefreshService } from "../../services/ciam-refresh.service";

@Injectable({ providedIn: 'root' })
export class AzureProvider implements AuthProvider {

    clientid = "l301s7obVzqJuKI4epxQQC5PPCQa";
    scope = "openid internal_org_user_mgt_view internal_org_user_mgt_list internal_org_user_mgt_create nternal_org_user_mgt_update internal_org_user_mgt_delete internal_user_mgt_view internal_user_mgt_list internal_user_mgt_create internal_user_mgt_update internal_user_mgt_delete";
    sessionid = "session-001";

    authenticatorId = "T3BlbklEQ29ubmVjdEF1dGhlbnRpY2F0b3I6aWRwQXp1cmVROE9pbHM";

    private readonly azureFlowIdStorageKey = "azure_flow_id";
    private readonly tokenAuthHeader = "Basic bDMwMXM3b2JWenFKdUtJNGVweFFRQzVQUENRYTphWUJLZk4xRzVIQUpmZWx6WXFKM1gycnF4bkZaM2FlVVNqWHhzVHZqbm1VYQ==";

    constructor(
        private http: HttpClient,
        private router: Router,
        private ciamRefreshService: CiamRefreshService,
    ) {}

    login(): Observable<AzureAuthResponse> {
        return this.startAzureLoginFlow();
    }

    completeLogin(code: string, state: string): Observable<AzureTokenResponse> {
        const flowId = localStorage.getItem(this.azureFlowIdStorageKey);

        if (!flowId) {
            throw new Error("Flow Azure non trovato in storage.");
        }

        const authBody: AzureAuthRequest = {
            flowId,
            selectedAuthenticator: {
                authenticatorId: this.authenticatorId,
                params: {
                    code,
                    state
                }
            }
        };

        return this.loginAuth(authBody).pipe(
            tap((authResponse) => {
                console.log("azureAuthCallbackResponse", authResponse);
            }),
            switchMap((authResponse) => {
                const ciamCode = authResponse.authData?.code;

                if (authResponse.flowStatus !== "SUCCESS_COMPLETED" || !ciamCode) {
                    throw new Error("Flusso Azure non completato correttamente.");
                }

                const tokenBody: AzureTokenRequest = {
                    granttype: "authorization_code",
                    code: ciamCode,
                    redirect_uri: this.redirectUri,
                    scope: this.scope
                };

                return this.loginToken(tokenBody);
            }),
            tap((tokenResponse) => {
                console.log("azureTokenResponse", tokenResponse);
                this.saveSession({
                    accessToken: tokenResponse.access_token,
                    refreshToken: tokenResponse.refresh_token,
                    expiresAt: tokenResponse.expires_in
                });
                localStorage.removeItem(this.azureFlowIdStorageKey);
            })
        );
    }

    getToken(): Observable<string | null> {
        return this.ciamRefreshService.getToken().pipe(
            catchError(() => {
                this.logout();
                return EMPTY;
            })
        );
    }

    private saveSession(res: AuthResponse) {
        localStorage.setItem(AUTH_KEYS.LOGIN_TYPE, LoginType.AZURE);
        localStorage.setItem(AUTH_KEYS.ACCESS_TOKEN, res.accessToken);
        if (res.refreshToken) {
            localStorage.setItem(AUTH_KEYS.REFRESH_TOKEN, res.refreshToken);
        }
        localStorage.setItem(
            AUTH_KEYS.EXPIRES_AT,
            (Date.now() + res.expiresAt * 1000).toString()
        );
    }

    isLoggedIn(): Observable<boolean> { return of(!!localStorage.getItem('access_token')); }

    logout() {
        localStorage.removeItem(AUTH_KEYS.LOGIN_TYPE);
        localStorage.removeItem(AUTH_KEYS.ACCESS_TOKEN);
        localStorage.removeItem(AUTH_KEYS.REFRESH_TOKEN);
        localStorage.removeItem(AUTH_KEYS.EXPIRES_AT);
        localStorage.removeItem(this.azureFlowIdStorageKey);
        this.router.navigate(['/login']);
    }

    // Chiamate API flusso
    private loginInit(request: AzureInitRequest): Observable<AzureInitResponse> {
        const url = 'https://ais-test.ciam.q8.com/ciamauthentication/init';
        return this.http.post<AzureInitResponse>(url, request, { context: this.getBypassAuthContext() });
    }

    private loginAuth(request: AzureAuthRequest): Observable<AzureAuthResponse> {
        const url = 'https://ais-test.ciam.q8.com/ciamauthentication/auth';
        return this.http.post<AzureAuthResponse>(url, request, { context: this.getBypassAuthContext() });
    }

    private loginToken(request: AzureTokenRequest): Observable<AzureTokenResponse> {
        const url = 'https://ais-test.ciam.q8.com/ciamauthentication/1.0.0/token';
        const headers = new HttpHeaders({
            Authorization: this.tokenAuthHeader
        });

        return this.http.post<AzureTokenResponse>(url, request, {
            headers,
            context: this.getBypassAuthContext()
        });
    }

    private getRedirectUrl(response: AzureAuthResponse): string | null {
        return response.nextStep?.authenticators?.[0]?.metadata?.additionalData?.redirectUrl ?? null;
    }

    private getBypassAuthContext(): HttpContext {
        return new HttpContext().set(BYPASS_AUTH, true);
    }

    private get redirectUri(): string {
        return new URL('/auth/callback', window.location.origin).toString();
    }

    private startAzureLoginFlow() {
        const initBody: AzureInitRequest = {
            clientid: this.clientid,
            redirect_uri: this.redirectUri,
            scope: this.scope,
            sessionid: this.sessionid
        };
    
        return this.loginInit(initBody).pipe(
            switchMap((initResponse) => {
                const flowId = initResponse.flowId;
                localStorage.setItem(this.azureFlowIdStorageKey, flowId);

                const paramsBody: AzureAuthRequest = {
                    flowId,
                    selectedAuthenticator: {
                        authenticatorId: this.authenticatorId
                    }
                };

                return this.loginAuth(paramsBody).pipe(
                    tap((authResponse) => {
                        console.log("azureAuthResponse", authResponse);

                        const redirectUrl = this.getRedirectUrl(authResponse);

                        if (!redirectUrl) {
                            throw new Error("redirectUrl non presente nella response Azure.");
                        }

                        window.location.assign(redirectUrl);
                    })
                );
            })
        );
    }
}
